/**
 * printHmisPeriod — build a print-ready HMIS return laid out section-by-section
 * like the MoH paper form. Uses the same hidden-iframe pattern as
 * printPatientChart.js — no popup blocker, no @media print CSS clipping.
 */
const esc = (s) => (s == null ? "" : String(s)).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const fmt = (iso) => { if (!iso) return "—"; try { const d = new Date(iso); return isNaN(d) ? esc(iso) : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return esc(iso); } };

export function printHmisPeriod({ period, values, template, sections, indicators, ageBands, organization, generatedBy }) {
  const orgName = esc(organization?.name || "Vitality Medical Software");
  const dhis2 = esc(organization?.dhis2_org_unit || "");
  const status = esc(period?.status || "Draft");
  const version = period?.version || 1;
  const gen = `${orgName} · Generated ${fmt(new Date().toISOString())}${generatedBy?.name ? ` by ${esc(generatedBy.name)}` : ""}${generatedBy?.role ? ` (${esc(generatedBy.role)})` : ""}`;

  // Bucket values by indicator_id, and inside by "band|sex"
  const valMap = {};
  (values || []).forEach(v => { (valMap[v.indicator_id] ||= []).push(v); });
  const indBySec = {};
  (indicators || []).forEach(i => { (indBySec[i.section_id] ||= []).push(i); });

  const sectionHtml = (sections || []).map(sec => {
    const inds = indBySec[sec.section_id] || [];
    if (inds.length === 0) return "";
    const rows = inds.flatMap(ind => {
      const vs = valMap[ind.indicator_id] || [{ age_band: "—", sex: "—", computed_value: 0, adjusted_value: null }];
      return vs.map((v, i) => {
        const final = v.adjusted_value != null ? v.adjusted_value : (v.computed_value ?? 0);
        const adj = v.adjusted_value != null;
        return `<tr>
          ${i === 0 ? `<td rowspan="${vs.length}" class="tabular code">${esc(ind.code || "")}</td><td rowspan="${vs.length}">${esc(ind.label || "")}</td>` : ""}
          <td class="muted">${esc(v.age_band)}</td>
          <td class="muted">${esc(v.sex)}</td>
          <td class="tabular right">${v.computed_value ?? "—"}</td>
          <td class="tabular right ${adj ? "adj" : ""}">${v.adjusted_value != null ? v.adjusted_value : "—"}</td>
          <td class="tabular right final"><b>${final}</b></td>
          ${adj ? `<td class="muted note">${esc(v.adjustment_reason || "")}</td>` : `<td class="muted note">—</td>`}
        </tr>`;
      });
    }).join("");
    return `
      <section>
        <h2>${esc(sec.title || "")}</h2>
        <table class="grid">
          <thead><tr>
            <th>Code</th><th>Indicator</th><th>Age band</th><th>Sex</th><th class="right">Computed</th><th class="right">Adjusted</th><th class="right">Final</th><th>Adjustment reason</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>`;
  }).join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(template?.code || "HMIS")} · ${esc(period?.period_start || "")}</title>
<style>
  @page { size: A4; margin: 15mm 12mm 18mm 12mm; }
  * { box-sizing: border-box; }
  html, body { font-family: 'Helvetica','Arial',sans-serif; font-size: 10pt; color:#0f172a; line-height:1.4; margin:0; }
  .brand { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #0b3d91; padding-bottom:6pt; margin-bottom:8pt; }
  .brand h1 { font-size:14pt; color:#0b3d91; font-weight:800; margin:0; }
  .doc-type { font-size:8pt; letter-spacing:3pt; text-transform:uppercase; color:#475569; }
  .gen-line { font-size:8pt; color:#475569; text-align:right; }
  .facility { border:1.5px solid #0b3d91; border-radius:6pt; padding:8pt 10pt; margin-bottom:10pt; background:#f8fafc; page-break-inside:avoid; }
  .facility-name { font-size:13pt; font-weight:800; color:#0b3d91; margin-bottom:4pt; }
  .facility-meta { display:flex; flex-wrap:wrap; gap:6pt 18pt; font-size:9pt; color:#334155; }
  .facility-meta b { color:#0f172a; font-weight:600; }
  .status-pill { display:inline-block; padding:1pt 8pt; border-radius:999pt; font-size:8pt; font-weight:700; }
  .status-Draft { background:#f1f5f9; color:#334155; }
  .status-UnderReview { background:#fef3c7; color:#92400e; }
  .status-Submitted { background:#dcfce7; color:#166534; }
  .status-Locked { background:#0f172a; color:#fff; }
  section { margin-bottom:10pt; page-break-inside:auto; }
  section h2 { font-size:11pt; color:#0b3d91; margin:8pt 0 4pt 0; padding-bottom:2pt; border-bottom:1px solid #cbd5e1; page-break-after:avoid; }
  table.grid { width:100%; border-collapse:collapse; font-size:8.5pt; }
  table.grid th { text-align:left; background:#0b3d91; color:#fff; padding:3pt 5pt; font-weight:600; }
  table.grid td { padding:3pt 5pt; border-bottom:1px solid #e2e8f0; vertical-align:top; }
  tr { page-break-inside:avoid; }
  .muted { color:#64748b; }
  .right { text-align:right; }
  .tabular { font-variant-numeric:tabular-nums; }
  .code { font-weight:600; color:#0b3d91; }
  .adj { color:#92400e; }
  .final { color:#0b3d91; }
  .note { font-size:7.5pt; font-style:italic; }
  .signature { margin-top:14pt; page-break-inside:avoid; }
  .sig-row { display:flex; gap:20pt; }
  .sig-cell { flex:1; }
  .sig-line { border-top:1px solid #0f172a; margin-top:28pt; }
  .sig-label { font-size:8.5pt; color:#334155; text-align:center; padding-top:3pt; font-weight:600; }
  .sig-sub { font-size:8pt; color:#64748b; text-align:center; }
  .foot { border-top:1px solid #e2e8f0; margin-top:10pt; padding-top:5pt; font-size:7.5pt; color:#64748b; text-align:center; }
</style></head><body>
  <div class="brand">
    <div><h1>${orgName}</h1><div class="doc-type">HMIS periodic return</div></div>
    <div class="gen-line">${esc(gen)}</div>
  </div>

  <div class="facility">
    <div class="facility-name">${orgName}</div>
    <div class="facility-meta">
      ${dhis2 ? `<span><b>DHIS2 org unit:</b> ${dhis2}</span>` : `<span><b>DHIS2 org unit:</b> —</span>`}
      <span><b>Template:</b> ${esc(template?.code || "?")} v${esc(template?.version || "?")} — ${esc(template?.name || "?")}</span>
      <span><b>Period:</b> ${esc(period?.period_start || "")} → ${esc(period?.period_end || "")}</span>
      <span><b>Version:</b> v${version}</span>
      <span><b>Status:</b> <span class="status-pill status-${status.replace(/ /g,'')}">${status}</span></span>
      ${period?.generated_at ? `<span><b>Generated:</b> ${fmt(period.generated_at)}${period.generated_by_name ? ` · ${esc(period.generated_by_name)}` : ""}</span>` : ""}
      ${period?.submitted_at ? `<span><b>Submitted:</b> ${fmt(period.submitted_at)}</span>` : ""}
    </div>
  </div>

  ${(sections || []).length ? sectionHtml : `<div class="muted" style="text-align:center;padding:20pt">No sections in this template.</div>`}

  <section class="signature">
    <div class="sig-row">
      <div class="sig-cell"><div class="sig-line"></div><div class="sig-label">Records Officer — signature</div><div class="sig-sub">Name &amp; date</div></div>
      <div class="sig-cell"><div class="sig-line"></div><div class="sig-label">Facility in-charge — signature &amp; stamp</div><div class="sig-sub">Name &amp; date</div></div>
    </div>
  </section>

  <div class="foot">Confidential — Vitality Medical Software · Uganda Data Protection and Privacy Act 2019.</div>
</body></html>`;

  const prev = document.getElementById("vms-hmis-print-frame");
  if (prev) prev.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "vms-hmis-print-frame";
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { console.error("HMIS print failed", e); }
    const cleanup = () => { const el = document.getElementById("vms-hmis-print-frame"); if (el) el.remove(); };
    try { iframe.contentWindow.addEventListener("afterprint", cleanup); } catch {}
    setTimeout(cleanup, 60000);
  };
  iframe.srcdoc = html;
}
